// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only.

/**
 * The enrollment form's §05 / §06 / §07 selection tokens — one JSON array per section on the drug
 * record (D-CRX-67). The grammar is shared with the PDF renderer (functions/CareRx/EnrollmentFormSpec.cs):
 *
 *   `anaph_dph_tabs`             the option is ticked
 *   `other:<text>`               the section's free-text "Other" line
 *   `qty:<key>=<text>`           the quantity printed for a ticked option, instead of the spec's
 *   `sig:<key>=<text>`           the sig printed for a ticked option, instead of the spec's
 *
 * Go-live #8 (Andrew): §07 needed the quantity on the form and an editable sig. Overrides ride in the
 * same array, as "Other" already does — no second column, no shape change for anything that reads it.
 */
export type OverrideKind = 'qty' | 'sig'

const prefix = (kind: OverrideKind) => `${kind}:`
const isOverride = (t: string) => t.startsWith('qty:') || t.startsWith('sig:')

/** Tokens that are orders (ticked options and "Other"), not overrides — what a section's count shows. */
export const orderTokens = (sel: string[]): string[] => sel.filter(t => !isOverride(t))

/** The override text for one option, or null when the spec's default prints. */
export function overrideOf(sel: string[], kind: OverrideKind, key: string): string | null {
  const head = `${prefix(kind)}${key}=`
  const t = sel.find(x => x.startsWith(head))
  return t === undefined ? null : t.slice(head.length)
}

/** Set (or, with blank / the default, clear) one option's override. Keeps every other token in place. */
export function withOverride(sel: string[], kind: OverrideKind, key: string, text: string | null, fallback?: string | null): string[] {
  const head = `${prefix(kind)}${key}=`
  const rest = sel.filter(x => !x.startsWith(head))
  const v = (text ?? '').trim()
  return v === '' || v === (fallback ?? '').trim() ? rest : [...rest, head + v]
}

/** Remove an option and its overrides — an override for an unticked option would be a stray order. */
export function withoutOption(sel: string[], key: string): string[] {
  return sel.filter(x => x !== key && !x.startsWith(`qty:${key}=`) && !x.startsWith(`sig:${key}=`))
}
